/**
 * API client for the admin-only User Management screen.
 */

import { authFetch, apiErrorMessage } from './authFetch';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

export interface AppUser {
    id: number;
    username: string;
    name: string | null;
    practice_scope: string;
    is_active: boolean;
    created_by: string | null;
    created_at: string | null;
    updated_at: string | null;
}

export interface UserCreateResponse {
    user: AppUser;
    generated_password: string;
}

export interface PasswordResetResponse {
    generated_password: string;
}

export const usersApi = {
    async list(): Promise<AppUser[]> {
        const response = await authFetch(`${API_BASE_URL}/users`);
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to load users'));
        }
        return response.json();
    },

    async create(username: string, name: string, practiceScope: string): Promise<UserCreateResponse> {
        const response = await authFetch(`${API_BASE_URL}/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, name, practice_scope: practiceScope }),
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to create user'));
        }
        return response.json();
    },

    async update(userId: number, updates: { name?: string; practice_scope?: string; is_active?: boolean }): Promise<AppUser> {
        const response = await authFetch(`${API_BASE_URL}/users/${userId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updates),
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to update user'));
        }
        return response.json();
    },

    async resetPassword(userId: number): Promise<PasswordResetResponse> {
        const response = await authFetch(`${API_BASE_URL}/users/${userId}/reset-password`, {
            method: 'POST',
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to reset password'));
        }
        return response.json();
    },
};
